import React, { useEffect, useState } from 'react';

const formatPrice = (n) => Number(n || 0).toFixed(2);

/**
 * "Remise" exit offer: when the visitor tries to leave the product page, a popup offers them
 * the product's discount. Once per view of the page: after it, the visitor leaves freely.
 * - back button: an extra history entry keeps the visitor on the page and the offer appears;
 * - closing the tab, refreshing, typing another address, an external link: the browser's own
 *   "Leave site?" box blocks the leave (browsers allow no other window there); if the visitor
 *   stays, the offer appears.
 * Browsers only allow both once the visitor has clicked, tapped or typed on the page, so the
 * history entry is added on that first gesture (one added without it is skipped by "back").
 * Links inside the site and tel:/mailto: links are not a leave.
 */
const ExitOfferPopup = ({ productId, percent, price, onAccept }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!productId || !(percent > 0)) return undefined;

    let shown = false;
    let trapArmed = false; // extra history entry on top: "back" stays on the page
    let ignoreNextPop = false;
    let notLeavingUntil = 0;

    const show = (fromBack) => {
      if (shown) return;
      shown = true;
      // Shown another way: drop the extra entry so the next "back" leaves at once.
      if (trapArmed && !fromBack) {
        ignoreNextPop = true;
        window.history.back();
      }
      trapArmed = false;
      setOpen(true);
    };

    const armTrap = () => {
      if (trapArmed || shown) return;
      trapArmed = true;
      window.history.pushState({ exitOffer: true }, '');
    };

    const onClick = (e) => {
      const link = e.target.closest?.('a[href]');
      if (!link) {
        armTrap();
        return;
      }
      // A link of the site, or one opening the phone/mail app, is not a leave.
      const url = new URL(link.href, window.location.href);
      if (url.origin === window.location.origin || !/^https?:$/.test(url.protocol)) {
        notLeavingUntil = Date.now() + 2000;
      }
    };
    const onKeyDown = () => armTrap();
    const onPopState = () => {
      if (ignoreNextPop) {
        ignoreNextPop = false;
        return;
      }
      if (trapArmed) {
        trapArmed = false;
        show(true);
      }
    };
    // The browser asks "Leave site?"; the timer only runs if the visitor stayed on the page.
    const onBeforeUnload = (e) => {
      if (shown || Date.now() < notLeavingUntil) return;
      e.preventDefault();
      e.returnValue = '';
      setTimeout(() => show(false), 0);
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [productId, percent]);

  if (!open) return null;

  const close = () => setOpen(false);
  const accept = () => {
    setOpen(false);
    onAccept();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={close}>
      <div dir="rtl" className="relative w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={close} aria-label="Fermer" className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <p className="text-sm font-semibold text-[#ff4d00]">انتظر!</p>
        <p className="mt-2 text-5xl font-extrabold text-gray-900" dir="ltr">-{percent}%</p>
        <h3 className="mt-3 text-lg font-bold text-gray-900">عرض خاص لك فقط</h3>
        <p className="mt-1 text-sm text-gray-600">اشترِ هذا المنتج الآن بخصم {percent}%</p>

        <div className="mt-4 flex items-baseline justify-center gap-3">
          <span className="text-lg text-gray-400 line-through">{formatPrice(price)} د.ت</span>
          <span className="text-2xl font-bold text-gray-900">{formatPrice(price * (1 - percent / 100))} د.ت</span>
        </div>

        <button
          type="button"
          onClick={accept}
          className="mt-5 w-full rounded-lg bg-[#4983a9] py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#3f7294]"
        >
          استفد من الخصم
        </button>
        <button type="button" onClick={close} className="mt-2 w-full py-2 text-sm text-gray-500 underline">
          لا، شكراً
        </button>
      </div>
    </div>
  );
};

export default ExitOfferPopup;
